import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { OrgProfile, PostalAddress } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { authStore } from "../../auth/store";
import { OrgLayout } from "../../components/Shell";
import { Field, PageHeader, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { SaveBar } from "../../components/UnsavedChanges";
import { OrgProfileFields, browserTimezone, normalizeProfile, postalOf, profileProblems, sameAddress, useAddressCheck } from "./OrgProfileFields";

/** A saved profile with the browser's zone and a starting country filled in when none is stored. */
function withZone(p: OrgProfile | undefined): OrgProfile {
  return { ...(p ?? {}), country: p?.country || "US", timezone: p?.timezone || browserTimezone() };
}

export function OrgSettingsPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const org = useQuery({ queryKey: queryKeys.org(orgId), queryFn: () => api.orgs.get(orgId) });
  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState("");
  const [profile, setProfile] = useState<OrgProfile | null>(null);
  const [savedProfile, setSavedProfile] = useState<OrgProfile | null>(null);
  useEffect(() => {
    if (org.data) {
      setName(org.data.name);
      setSavedName(org.data.name);
      const p = withZone(org.data.profile);
      setProfile(p);
      setSavedProfile(p);
    }
  }, [org.data]);
  const errors = profile ? profileProblems(profile) : {};
  const invalid = !name.trim() || Object.keys(errors).length > 0;
  const dirty = name !== savedName || (!!profile && !!savedProfile && JSON.stringify(normalizeProfile(profile)) !== JSON.stringify(normalizeProfile(savedProfile)));

  const address = useAddressCheck(useCallback((a: PostalAddress) => api.orgs.lookupAddress(orgId, a), [orgId]));
  const save = useMutation({
    mutationFn: (p: OrgProfile) => api.orgs.patch(orgId, { name: name.trim(), profile: normalizeProfile(p) }),
    onSuccess: (o) => {
      setName(o.name);
      setSavedName(o.name);
      const p = withZone(o.profile);
      setProfile(p);
      setSavedProfile(p);
      void qc.invalidateQueries({ queryKey: queryKeys.org(orgId) });
      void authStore.load();
      toast.push("success", "Organization saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  // The address is only checked when it changed; renaming the organization
  // should not bring the suggestion back up.
  const onSave = async () => {
    if (!profile) return;
    const chosen = sameAddress(postalOf(profile), postalOf(savedProfile ?? undefined)) ? profile : await address.confirm(profile);
    if (!chosen) return;
    setProfile(chosen);
    save.mutate(chosen);
  };

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="General" />
      {org.isPending || !profile ? (
        <Skeleton />
      ) : org.isError ? (
        <ErrorState error={org.error} retry={() => void org.refetch()} />
      ) : (
        <>
          <div className="card form-card">
            <section className="section">
              <h2>Organization</h2>
              <div className="settings-stack">
                <Field label="Name" error={name.trim() ? undefined : "Enter a name."}>
                  {(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 360 }} />}
                </Field>
              </div>
            </section>
            <section className="section">
              <h2>Contact and address</h2>
              <div className="settings-stack">
                <OrgProfileFields value={profile} onChange={setProfile} errors={errors} />
              </div>
            </section>
          </div>
          <SaveBar
            dirty={dirty}
            saving={save.isPending || address.checking}
            disabled={invalid}
            onSave={() => void onSave()}
            onDiscard={() => {
              setName(savedName);
              if (savedProfile) setProfile(savedProfile);
            }}
          />
          {address.dialog}
        </>
      )}
    </OrgLayout>
  );
}
